import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Layout from '../components/Layout';
import api, { categoriesAPI } from '../services/api';
import { useToast } from '../components/Toast';
import {
  Search, Plus, Edit2, Trash2, RefreshCw, X,
  Tag, ToggleLeft, ToggleRight, Image, Package
} from 'lucide-react';

const IMAGE_BASE = (() => {
  const raw = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
  return raw.replace(/\/api\/?$/, '');
})();

const STATUS_OPTS = ['ALL', 'ACTIVE', 'INACTIVE'];

function resolveImg(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('data:')) return s;
  if (s.startsWith('/')) return IMAGE_BASE + '/api' + s;
  return `${IMAGE_BASE}/uploads/${s}`;
}

// ── Extracted: build product count maps from products array ──────────────────
function buildProductCountMaps(products) {
  const countById   = {};
  const countByName = {};
  products.forEach(p => {
    if (p.categoryId) {
      countById[p.categoryId] = (countById[p.categoryId] || 0) + 1;
    }
    if (p.category) {
      const key = p.category.trim().toLowerCase();
      countByName[key] = (countByName[key] || 0) + 1;
    }
  });
  return { countById, countByName };
}

// ── Extracted: merge product counts into categories ──────────────────────────
function mergeProductCounts(cats, products) {
  const { countById, countByName } = buildProductCountMaps(products);
  return cats.map(c => {
    const backendCount = c.productCount ?? c.totalProducts ?? c.noOfProducts ?? c.products ?? null;
    const nameKey      = c.name?.trim().toLowerCase();
    const computed     = countById[c.id] ?? countByName[nameKey] ?? 0;
    return {
      ...c,
      productCount: backendCount && backendCount > 0 ? backendCount : computed,
    };
  });
}

// ── Extracted: fetch categories from API ─────────────────────────────────────
async function fetchCategories(statusFilter) {
  const status = statusFilter === 'ALL' ? null : statusFilter;
  const res    = await categoriesAPI.getAll(0, 200, status);
  const body   = res.data;
  return { cats: body.data || body.content || [], total: body.totalElements };
}

// ── Extracted: fetch products from API ───────────────────────────────────────
async function fetchProducts() {
  const res = await api.get('/products', { params: { page: 0, size: 1000 } });
  return res.data?.content || res.data || [];
}

// ── Extracted: resolve products from settled promise ─────────────────────────
function resolveProducts(prodResult) {
  if (prodResult.status !== 'fulfilled') return [];
  return prodResult.value.data?.content || prodResult.value.data || [];
}

// ── Extracted: enrich categories with product counts ─────────────────────────
function enrichCategories(cats, prodResult) {
  const products = resolveProducts(prodResult);
  if (!products.length) return cats;
  return mergeProductCounts(cats, products);
}

// ── CategoryModal ─────────────────────────────────────────────────────────────
function CategoryModal({ category, existingCategories, onSave, onClose, loading }) {
  const isEdit = !!category;
  const [name,      setName]      = useState(category?.name   || '');
  const [image,     setImage]     = useState(category?.image  || ''); // preview only (base64 or existing URL)
  const [imageFile, setImageFile] = useState(null);                   // actual File for upload
  const [status,    setStatus]    = useState(category?.status || '');
  const [dragOver,  setDragOver]  = useState(false);
  const [touched,   setTouched]   = useState({ name: false, image: false, status: false });
  const fileInputRef = React.useRef(null);

  const nameTrimmed = name.trim();

  const isDuplicate = !isEdit && existingCategories.some(
    c => c.name.trim().toLowerCase() === nameTrimmed.toLowerCase()
  );
  const isDuplicateEdit = isEdit && existingCategories.some(
    c => c.id !== category.id && c.name.trim().toLowerCase() === nameTrimmed.toLowerCase()
  );

  const getNameError = () => {
    if (nameTrimmed) {
      if (isDuplicate || isDuplicateEdit) return `"${nameTrimmed}" already exists`;
      return '';
    }
    return 'Category name is required';
  };

  const errors = {
    name:   getNameError(),
    image:  image  ? '' : 'Please upload a category image',
    status: status ? '' : 'Please select a status',
  };

  const isValid = !errors.name && !errors.image && !errors.status;

  const touchAll = () => setTouched({ name: true, image: true, status: true });

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setTouched(t => ({ ...t, image: true }));
      return;
    }
    setImageFile(file); // store File object for upload
    const reader = new FileReader();
    reader.onload = (e) => {
      setImage(e.target.result); // base64 for preview only
      setTouched(t => ({ ...t, image: true }));
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleRemoveImage = () => {
    setImage('');
    setImageFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setTouched(t => ({ ...t, image: true }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    touchAll();
    if (isValid) {
      // Pass imageFile (File object) — NOT the base64 image string.
      // The base64 `image` is only used for the preview above.
      onSave({ name: nameTrimmed, imageFile, status });
    }
  };

  const getDropZoneBorder = () => {
    if (dragOver) return 'var(--primary)';
    if (touched.image && errors.image) return 'var(--red)';
    return 'var(--border)';
  };
  const dropZoneBorder = getDropZoneBorder();

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">{isEdit ? 'Edit Category' : 'New Category'}</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          {/* Name */}
          <div className="form-group">
            <label htmlFor="cat-name">Category Name <span style={{ color: 'var(--red)' }}>*</span></label>
            <input
              id="cat-name"
              value={name}
              onChange={e => { setName(e.target.value); setTouched(t => ({ ...t, name: true })); }}
              onBlur={() => setTouched(t => ({ ...t, name: true }))}
              placeholder="e.g. Pickles & Chutneys"
              autoFocus
              style={touched.name && errors.name ? { borderColor: 'var(--red)', boxShadow: '0 0 0 3px rgba(239,68,68,0.12)' } : {}}
            />
            {touched.name && errors.name && (
              <p style={{ color: 'var(--red)', fontSize: 12, marginTop: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
                ⚠ {errors.name}
              </p>
            )}
          </div>

          {/* Image */}
          <div className="form-group">
            <label htmlFor="cat-image">Category Image <span style={{ color: 'var(--red)' }}>*</span></label>

            {image ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 14px', borderRadius: 10, border: '2px solid var(--green)', background: 'rgba(34,197,94,0.05)' }}>
                <div style={{ width: 72, height: 72, borderRadius: 8, overflow: 'hidden', flexShrink: 0, border: '1px solid var(--border)' }}>
                  <img src={image} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--green)', marginBottom: 4 }}>✓ Image selected</p>
                  {imageFile && (
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {imageFile.name} ({(imageFile.size / 1024).toFixed(1)} KB)
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => { handleRemoveImage(); fileInputRef.current?.click(); }}
                    style={{ marginTop: 6, fontSize: 12, color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                  >
                    Change image
                  </button>
                </div>
                <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={handleRemoveImage} title="Remove image">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border:       `2px dashed ${dropZoneBorder}`,
                  borderRadius: 10,
                  padding:      '28px 20px',
                  width:        '100%',
                  textAlign:    'center',
                  cursor:       'pointer',
                  background:   dragOver ? 'var(--primary-glow-sm)' : 'var(--bg-hover)',
                  transition:   'border-color 0.2s, background 0.2s',
                }}
              >
                <Image size={28} style={{ color: dragOver ? 'var(--primary)' : 'var(--text-muted)', marginBottom: 8 }} />
                <p style={{ fontSize: 13, fontWeight: 600, color: dragOver ? 'var(--primary)' : 'var(--text-secondary)', marginBottom: 4 }}>
                  {dragOver ? 'Drop to upload' : 'Click or drag & drop image here'}
                </p>
                <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>PNG, JPG, WEBP · Max 5 MB</p>
              </button>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={e => handleFile(e.target.files[0])}
            />

            {touched.image && errors.image && (
              <p style={{ color: 'var(--red)', fontSize: 12, marginTop: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
                ⚠ {errors.image}
              </p>
            )}
          </div>

          {/* Status */}
          <div className="form-group">
            <label htmlFor="cat-status">Status <span style={{ color: 'var(--red)' }}>*</span></label>
            <select
              id="cat-status"
              value={status}
              onChange={e => { setStatus(e.target.value); setTouched(t => ({ ...t, status: true })); }}
              onBlur={() => setTouched(t => ({ ...t, status: true }))}
              style={touched.status && errors.status ? { borderColor: 'var(--red)', boxShadow: '0 0 0 3px rgba(239,68,68,0.12)' } : {}}
            >
              <option value="">— Select status —</option>
              <option value="ACTIVE">ACTIVE</option>
              <option value="INACTIVE">INACTIVE</option>
            </select>
            {touched.status && errors.status && (
              <p style={{ color: 'var(--red)', fontSize: 12, marginTop: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
                ⚠ {errors.status}
              </p>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
              onClick={touchAll}
            >
              {(() => {
                if (loading) return 'Saving...';
                return isEdit ? 'Save Changes' : 'Create Category';
              })()}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

CategoryModal.propTypes = {
  category: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    image: PropTypes.string,
    status: PropTypes.string,
  }),
  existingCategories: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  })).isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

CategoryModal.defaultProps = {
  category: null,
};

// ── DeleteModal ───────────────────────────────────────────────────────────────
function DeleteModal({ category, onConfirm, onClose, loading }) {
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <span className="modal-title">Delete Category</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ padding: '8px 0 20px' }}>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Are you sure you want to delete{' '}
            <strong style={{ color: 'var(--text-primary)' }}>"{category.name}"</strong>?
            {category.productCount > 0 && (
              <span style={{ color: 'var(--red)' }}>
                {' '}This category has {category.productCount} product{category.productCount === 1 ? '' : 's'} associated with it.
              </span>
            )}
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={loading}>
            {loading ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

DeleteModal.propTypes = {
  category: PropTypes.shape({
    name: PropTypes.string,
    productCount: PropTypes.number,
  }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

// ── CategoryCard ──────────────────────────────────────────────────────────────
function CategoryCard({ category, onEdit, onDelete, onToggleStatus, toggling }) {
  const isActive = category.status === 'ACTIVE';

  const handleMouseEnter = (e) => { e.currentTarget.style.transform = 'translateY(-2px)'; };
  const handleMouseLeave = (e) => { e.currentTarget.style.transform = 'translateY(0)'; };

  return (
    <div
      className="card"
      style={{ padding: 0, overflow: 'hidden', transition: 'transform 0.2s, border-color 0.2s' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <CategoryCardImage category={category} />

      <div style={{ padding: '14px 16px' }}>
        <div style={{ marginBottom: 8 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
            {category.name}
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Package size={12} style={{ color: 'var(--text-muted)' }} />
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {category.productCount ?? 0} product{category.productCount === 1 ? '' : 's'}
            </span>
          </div>
        </div>

        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12 }}>
          Created {category.createdAt ? new Date(category.createdAt).toLocaleDateString('en-IN') : '—'}
        </div>

        <div style={{ display: 'flex', gap: 6 }}>
          <button
            className="btn btn-ghost btn-sm"
            style={{ flex: 1, justifyContent: 'center' }}
            onClick={() => onToggleStatus(category)}
            disabled={toggling}
            title={isActive ? 'Deactivate' : 'Activate'}
          >
            {isActive
              ? <><ToggleRight size={14} style={{ color: 'var(--green)' }} /> Active</>
              : <><ToggleLeft  size={14} style={{ color: 'var(--text-muted)' }} /> Inactive</>}
          </button>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onEdit(category)} title="Edit">
            <Edit2 size={14} />
          </button>
          <button className="btn btn-danger btn-icon btn-sm" onClick={() => onDelete(category)} title="Delete">
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

CategoryCard.propTypes = {
  category: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    image: PropTypes.string,
    status: PropTypes.string,
    productCount: PropTypes.number,
    createdAt: PropTypes.string,
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onToggleStatus: PropTypes.func.isRequired,
  toggling: PropTypes.bool.isRequired,
};

// ── Extracted: card image section ─────────────────────────────────────────────
function CategoryCardImage({ category }) {
  const handleImgError = (e) => {
    e.target.parentNode.style.background = 'var(--bg-hover)';
    e.target.style.display = 'none';
  };

  return (
    <div style={{ height: 120, background: 'var(--bg-hover)', position: 'relative', overflow: 'hidden' }}>
      {category.image ? (
        <img
          src={resolveImg(category.image)}
          alt={category.name}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={handleImgError}
        />
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Tag size={36} style={{ color: 'var(--text-muted)', opacity: 0.4 }} />
        </div>
      )}
    </div>
  );
}

CategoryCardImage.propTypes = {
  category: PropTypes.shape({
    image: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
};

// ── Extracted: load result processor ──────────────────────────────────────────
function processCategoryLoadResult(catRes, prodRes) {
  if (catRes.status !== 'fulfilled') return null;
  const { cats, total } = catRes.value;
  const enriched = enrichCategories(cats, prodRes);
  return { enriched, total: total || enriched.length };
}

// ── Main Categories Page ──────────────────────────────────────────────────────
export default function Categories() {
  const [categories,    setCategories]    = useState([]);
  const [total,         setTotal]         = useState(0);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState('');
  const [statusFilter,  setStatusFilter]  = useState('ALL');
  const [modalState,    setModalState]    = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [togglingId,    setTogglingId]    = useState(null);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, prodRes] = await Promise.allSettled([
        fetchCategories(statusFilter),
        fetchProducts(),
      ]);

      const result = processCategoryLoadResult(catRes, prodRes);

      if (!result) {
        toast('Failed to load categories', 'error');
        return;
      }

      setCategories(result.enriched);
      setTotal(result.total);
    } catch {
      toast('Failed to load categories', 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, toast]);

  useEffect(() => { load(); }, [load]);

  // ── Save (create / edit) ────────────────────────────────────────────────────
  // FIX: removed uploadImageIfNeeded — the backend (CategoryController) handles
  // S3 upload itself. We pass imageFile (File object) directly via FormData.
  // Previously this was double-uploading: once via /uploads then again via
  // /admin/categories, and also incorrectly passing base64 strings as the image.
  const handleSave = async ({ name, imageFile, status }) => {
    setActionLoading(true);
    try {
      if (modalState.mode === 'create') {
        await categoriesAPI.create(name, imageFile, status);
        toast('Category created successfully', 'success');
      } else {
        await categoriesAPI.update(modalState.category.id, { name, image: imageFile, status });
        toast('Category updated successfully', 'success');
      }
      setModalState(null);
      load();
    } catch (err) {
      const msg = err.response?.data?.message || `Failed to ${modalState.mode} category`;
      toast(msg, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await categoriesAPI.delete(modalState.category.id);
      toast(`"${modalState.category.name}" deleted`, 'info');
      setModalState(null);
      load();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to delete category';
      toast(msg, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // ── Toggle status ───────────────────────────────────────────────────────────
  const handleToggleStatus = async (category) => {
    setTogglingId(category.id);
    const newStatus = category.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    try {
      await categoriesAPI.update(category.id, { status: newStatus });
      toast(`"${category.name}" set to ${newStatus}`, 'success');
      setCategories(prev =>
        prev.map(c => c.id === category.id ? { ...c, status: newStatus } : c)
      );
    } catch {
      toast('Failed to update status', 'error');
    } finally {
      setTogglingId(null);
    }
  };

  const filtered = categories.filter(c => {
    const q = search.toLowerCase();
    if (!q) return true;
    return c.name?.toLowerCase().includes(q);
  });

  const activeCount   = categories.filter(c => c.status === 'ACTIVE').length;
  const inactiveCount = categories.filter(c => c.status === 'INACTIVE').length;

  return (
    <Layout title="Categories" subtitle="Manage product categories">
      {/* Top bar */}
      <div className="cat-topbar">
        <div className="cat-topbar-row1">
          <div className="search-box cat-search">
            <Search size={14} className="search-icon" />
            <input placeholder="Search categories..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="btn btn-ghost btn-sm" onClick={load}><RefreshCw size={13} /></button>
          <button className="btn btn-primary btn-sm" onClick={() => setModalState({ mode: 'create' })}>
            <Plus size={15} /> New Category
          </button>
        </div>
        <div className="chip-group cat-chips" style={{ margin: 0 }}>
          {STATUS_OPTS.map(s => (
            <button key={s} className={`chip ${statusFilter === s ? 'active' : ''}`} onClick={() => setStatusFilter(s)}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
        {[
          { label: 'Total',    value: total,         color: 'var(--text-primary)' },
          { label: 'Active',   value: activeCount,   color: 'var(--green)' },
          { label: 'Inactive', value: inactiveCount, color: 'var(--text-muted)' },
        ].map(s => (
          <div key={s.label} style={{ padding: '10px 18px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* Content */}
      {loading && (
        <div className="page-loader"><div className="spinner" /></div>
      )}
      {!loading && filtered.length === 0 && (
        <div className="empty-state" style={{ marginTop: 60 }}>
          <Tag size={48} />
          <h3>No categories found</h3>
          <p>{search ? 'Try a different search term' : 'Create your first category to get started'}</p>
          {!search && (
            <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setModalState({ mode: 'create' })}>
              <Plus size={14} /> Create Category
            </button>
          )}
        </div>
      )}
      {!loading && filtered.length > 0 && (
        <div className="cat-grid">
          {filtered.map(cat => (
            <CategoryCard
              key={cat.id}
              category={cat}
              onEdit={c => setModalState({ mode: 'edit', category: c })}
              onDelete={c => setModalState({ mode: 'delete', category: c })}
              onToggleStatus={handleToggleStatus}
              toggling={togglingId === cat.id}
            />
          ))}
        </div>
      )}

      {filtered.length > 0 && (
        <div style={{ marginTop: 16, fontSize: 13, color: 'var(--text-muted)' }}>
          Showing {filtered.length} of {total} categories
        </div>
      )}

      {(modalState?.mode === 'create' || modalState?.mode === 'edit') && (
        <CategoryModal
          category={modalState.mode === 'edit' ? modalState.category : null}
          existingCategories={categories}
          onSave={handleSave}
          onClose={() => setModalState(null)}
          loading={actionLoading}
        />
      )}
      {modalState?.mode === 'delete' && (
        <DeleteModal
          category={modalState.category}
          onConfirm={handleDelete}
          onClose={() => setModalState(null)}
          loading={actionLoading}
        />
      )}
    </Layout>
  );
}